// src/slices/emailSlice.js
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';

// Async thunk to fetch emails
export const fetchEmails = createAsyncThunk(
  'emails/fetchEmails',
  async ({ mailbox, customerEmail }, { getState, rejectWithValue }) => {
    const token = getState().auth.token;
    const userId = getState().auth.user_id;
    try {
      const queryParams = new URLSearchParams();
      queryParams.append('box', mailbox);
      if (customerEmail) queryParams.append('customer', customerEmail);
      if (userId) queryParams.append('user_id', userId);

      const response = await axios.get(
        `https://portal.ttpadvisory.co.uk:3556/api/crmemails/fetch-emails?${queryParams.toString()}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      return response.data;
    } catch (err) {
      return [];
      // return rejectWithValue(err.response?.data?.error || 'Failed to fetch emails');
    }
  }
);

export const fetchEmailContent = createAsyncThunk(
  'emails/fetchEmailContent',
  async ({ uid, box }, { getState, rejectWithValue }) => {
    const token = getState().auth.token;
    const userId = getState().auth.user_id;
    try {
      const queryParams = new URLSearchParams();
      queryParams.append('uid', uid);
      queryParams.append('box', box);
      queryParams.append('user_id', userId);

      const response = await axios.get(
        `https://portal.ttpadvisory.co.uk:3556/api/crmemails/email-content?${queryParams.toString()}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return { uid, content: response.data };
    } catch (err) {
      return rejectWithValue(err.response?.data?.error || 'Failed to fetch email content');
    }
  }
);

// Async thunk to send an email
export const sendEmail = createAsyncThunk(
  'emails/sendEmail',
  async (emailData, { getState, rejectWithValue }) => {
    const token = getState().auth.token;
    const user_id = getState().auth.user_id;
    const data = {...emailData, user_id}

    try {
      const response = await axios.post(
        'https://portal.ttpadvisory.co.uk:3556/api/crmemails/send-email',
        data,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.error || 'Failed to send email');
    }
  }
);

// Async thunk to fetch folder structure
export const fetchFolders = createAsyncThunk(
  'emails/fetchFolders',
  async (_, { getState, rejectWithValue }) => {
    const token = getState().auth.token;
    const userId = getState().auth.user_id;
    try {
      const response = await axios.get(
        `https://portal.ttpadvisory.co.uk:3556/api/crmemails/folders/${userId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return response.data;
    } catch (err) {
      return [];
      //return rejectWithValue(err.response?.data?.error || 'Failed to fetch folders');
    }
  }
);

const emailSlice = createSlice({
  name: 'emails',
  initialState: {
    emails: [],
    folders: [],
    emailBodies: {},
    selectedEmail: null,
    mailbox: 'inbox',
    replyMode: null,
    sortField: 'date',
    sortDirection: 'desc',
    status: 'idle',
    error: null,
  },
  reducers: {
    selectEmail: (state, action) => {
      state.selectedEmail = action.payload;
    },
    clearSelectedEmail: (state) => {
      state.selectedEmail = null;
      state.replyMode = null;
    },
    setMailbox: (state, action) => {
      state.mailbox = action.payload;
    },
    startCompose: (state) => {
      state.replyMode = 'compose';
      state.selectedEmail = null;
    },
    startReply: (state, action) => {
      state.selectedEmail = action.payload;
      state.replyMode = 'reply';
    },
    startForward: (state, action) => {
      state.selectedEmail = action.payload;
      state.replyMode = 'forward';
    },
    cancelCompose: (state) => {
      state.replyMode = null;
      state.selectedEmail = null;
    },
    setSort: (state, action) => {
      const { field } = action.payload;
      if (state.sortField === field) {
        // toggle direction
        state.sortDirection = state.sortDirection === 'asc' ? 'desc' : 'asc';
      } else {
        state.sortField = field;
        state.sortDirection = 'asc';
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchEmails.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(fetchEmails.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.emails = action.payload;
      })
      .addCase(fetchEmails.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload || action.error.message;
      })
      .addCase(sendEmail.fulfilled, (state) => {
        state.status = 'idle';
      })
      .addCase(sendEmail.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload || action.error.message;
      })
      .addCase(fetchFolders.fulfilled, (state, action) => {
        state.folders = action.payload;
      })
      .addCase(fetchEmailContent.fulfilled, (state, action) => {
        const { uid, content } = action.payload;
        state.emailBodies[uid] = content;
      });
  },
});

export const {
  selectEmail,
  clearSelectedEmail,
  setMailbox,
  startCompose,
  startReply,
  startForward,
  cancelCompose,
  setSort,
} = emailSlice.actions;

export const selectEmailBodyByUid = (state, uid) => state.emails.emailBodies[uid];

export default emailSlice.reducer;
